import { useFormik } from "formik";
import { useEffect, useState } from "react";
import alertAwareLogo from "../../alertaware_logo_large.png";
import Moment from "moment";
import { useNavigate } from "react-router-dom";
import { useDarkMode } from "../../hooks/useDarkMode";

// For dev : https://9n3q6h7pi5.execute-api.us-east-2.amazonaws.com/Prod
// For qa : https://n0guahsv6i.execute-api.us-west-2.amazonaws.com/Prod
// For prod : https://tjd6dn32xd.execute-api.us-east-2.amazonaws.com/prod

// Switch to API_URLS.qa or API_URLS.prod before deploying to those environments
const API_URLS = {
  dev: "https://9n3q6h7pi5.execute-api.us-east-2.amazonaws.com/Prod",
  qa: "https://notification-api-qa.alertaware.com",
  staging: "https://notification-api-staging.alertaware.com",
  prod: "https://notification-api-prod.alertaware.com",
} as const;

const baseURL = API_URLS.staging;

async function fetchNotification(id: string) {
  const url = `${baseURL}/get-status?id=${id}`;
  const response = await fetch(url);
  return response.json();
}

async function updateNotification(
  id: string,
  userResponse: string,
  comment: string,
  userResponseCode: string,
) {
  const url = `${baseURL}/update-response?id=${id}`;
  const now = new Date();
  const body = {
    replied_at: now,
    confirmed_at: now,
    replied_note: comment,
    reply: userResponse,
    reply_code: userResponseCode,
  };
  return fetch(url, {
    body: JSON.stringify(body),
    method: "post",
    headers: { "Content-Type": "application/json" },
    mode: "no-cors",
  });
}

function getCodeFromResponse(response: string, pollOptions: any[]) {
  for (const option of pollOptions) {
    if (option.description === response) return option.code;
  }
  return "";
}

function getAttachmentType(url: string): "image" | "video" | "other" {
  const ext = url.split("?")[0].toLowerCase().split(".").pop() ?? "";
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp", "avif", "svg"].includes(ext))
    return "image";
  if (["mp4", "webm", "ogg", "mov", "avi", "mkv"].includes(ext)) return "video";
  return "other";
}

function linkifyText(text: string): (string | JSX.Element)[] {
  if (!text) return [text];
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const trailingPunctRegex = /[).,;:!?'"]+$/;
  const result: (string | JSX.Element)[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }

    let url = match[0];
    let trailing = "";
    const trailingMatch = url.match(trailingPunctRegex);
    if (trailingMatch) {
      trailing = trailingMatch[0];
      url = url.slice(0, url.length - trailing.length);
    }

    const href = url.toLowerCase().startsWith("www.") ? `https://${url}` : url;
    result.push(
      <a
        key={key++}
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-orange-600 dark:text-orange-400 underline hover:text-orange-700 dark:hover:text-orange-300 break-all"
      >
        {url}
      </a>,
    );
    if (trailing) result.push(trailing);

    lastIndex = urlRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result;
}

function getInitials(name: string): string {
  if (!name) return "?";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
}

function DarkModeToggle({
  isDark,
  toggle,
}: {
  isDark: boolean;
  toggle: () => void;
}) {
  return (
    <button
      onClick={toggle}
      aria-label="Toggle dark mode"
      className="fixed top-4 right-4 z-50 p-2.5 rounded-full bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
    >
      {isDark ? (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      ) : (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
          />
        </svg>
      )}
    </button>
  );
}

function PageLoadingView({
  isDark,
  toggle,
}: {
  isDark: boolean;
  toggle: () => void;
}) {
  return (
    <div className="min-h-screen bg-slate-100 dark:bg-gray-950 flex flex-col items-center justify-center gap-3 transition-colors duration-200">
      <DarkModeToggle isDark={isDark} toggle={toggle} />
      <svg
        aria-hidden="true"
        className="w-10 h-10 text-gray-300 dark:text-gray-700 animate-spin fill-orange-500"
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
      <p className="text-sm font-medium text-gray-400 dark:text-gray-500">
        Loading notification...
      </p>
    </div>
  );
}

function ButtonSpinner() {
  return (
    <div role="status">
      <svg
        aria-hidden="true"
        className="w-4 h-4 text-gray-900/30 animate-spin fill-gray-900"
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
      <span className="sr-only">Loading...</span>
    </div>
  );
}

function PollDisplayer() {
  const { isDark, toggle } = useDarkMode();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [logoError, setLogoError] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryParameters = new URLSearchParams(window.location.search);
  const notificationId: string = queryParameters.get("id") ?? "";

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      comment: "",
      selectedAnswer: "",
      pollOptions: [],
      attachement: [],
      repliedAt: "",
      organizationName: "",
      organizationSmallLogo: alertAwareLogo,
      createdAt: "",
      hide_comment_box: false,
      confirmed_at: null,
      hide_confirmation: false,
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      setLoading(true);
      const responseCode = getCodeFromResponse(
        formik.values.selectedAnswer,
        formik.values.pollOptions,
      );
      await updateNotification(
        notificationId,
        formik.values.selectedAnswer,
        formik.values.comment,
        responseCode,
      );
      setLoading(false);
      navigate(`/success?id=${notificationId}`);
    },
  });

  useEffect(() => {
    setLogoError(false);
  }, [formik.values.organizationSmallLogo]);

  useEffect(() => {
    fetchNotification(notificationId).then((v) => {
      if (v.uuid === undefined) {
        navigate("/not-found");
      } else {
        if (formik.values.title === "" || formik.values.description === "") {
          formik.setFieldValue("title", v.notification_detail.title);
          formik.setFieldValue("description", v.notification_detail.body);
          formik.setFieldValue(
            "organizationSmallLogo",
            v?.organization_banner_logo || "",
          );
          formik.setFieldValue("organizationName", v?.organization_name);

          const responseOptions = v.notification_detail.responseOptions;
          if (responseOptions?.length) {
            responseOptions.sort((a: any, b: any) => {
              const aCode = a?.code;
              const bCode = b?.code;
              const aParsed = parseInt(aCode, 10);
              const bParsed = parseInt(bCode, 10);
              const aIsNumber = !isNaN(aParsed);
              const bIsNumber = !isNaN(bParsed);
              if (aIsNumber && bIsNumber) return aParsed - bParsed;
              else if (aIsNumber) return -1;
              else if (bIsNumber) return 1;
              else return aCode?.localeCompare(bCode);
            });
          }

          formik.setFieldValue("pollOptions", responseOptions);
          formik.setFieldValue(
            "attachement",
            v.notification_detail.attachements,
          );
          formik.setFieldValue("selectedAnswer", v.reply);
          formik.setFieldValue("comment", v.replied_note);
          formik.setFieldValue("repliedAt", v.replied_at);
          formik.setFieldValue("createdAt", v.created_at);
          formik.setFieldValue("hide_comment_box", v.hide_comment_box);
          formik.setFieldValue("confirmed_at", v.confirmed_at);
          formik.setFieldValue("hide_confirmation", v.hide_confirmation);

          setPageLoading(false);
        }
        document.title =
          v?.notification_detail?.responseOptions?.length > 0
            ? "Answer Poll"
            : "Confirm Message";
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function renderConfirmed() {
    if (formik.values.confirmed_at !== null && formik.values.hide_comment_box) {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-full text-xs font-semibold text-green-700 dark:text-green-400">
          <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          Confirmed
        </span>
      );
    }
  }

  function renderMetadata() {
    const hasReply =
      formik.values.repliedAt !== null &&
      formik.values.repliedAt !== undefined &&
      formik.values.repliedAt !== "";
    return (
      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-800">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider flex-shrink-0">
            Created
          </span>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap">
            {Moment(formik.values.createdAt).format("MMM D, YYYY · h:mm a")}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {Moment(formik.values.createdAt).fromNow()}
          </span>
        </div>
        {hasReply && (
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider flex-shrink-0">
              Last Reply
            </span>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap">
              {Moment(formik.values.repliedAt).format("MMM D, YYYY · h:mm a")}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {Moment(formik.values.repliedAt).fromNow()}
            </span>
            {renderConfirmed()}
          </div>
        )}
      </div>
    );
  }

  function renderPollOptions() {
    if (!formik.values.pollOptions) return null;
    return (
      <fieldset className="space-y-2">
        {(formik.values.pollOptions as any[]).map((v: any, idx: number) => {
          const isSelected = v.description === formik.values.selectedAnswer;
          return (
            <div key={v.description}>
              <input
                className="sr-only"
                type="radio"
                id={`poll-option-${idx}`}
                name="pollAnswer"
                checked={isSelected}
                value={v.description}
                onChange={() =>
                  formik.setFieldValue("selectedAnswer", v.description)
                }
              />
              <label
                htmlFor={`poll-option-${idx}`}
                className={`flex items-center gap-3 px-4 py-3.5 border rounded-xl cursor-pointer text-sm select-none transition-all duration-150 ${
                  isSelected
                    ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-gray-800 dark:text-gray-100 font-semibold shadow-sm"
                    : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-orange-300 dark:hover:border-orange-500/40 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                }`}
              >
                <span
                  className={`w-4 h-4 flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${
                    isSelected
                      ? "border-orange-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {isSelected && (
                    <span className="w-2 h-2 rounded-full bg-orange-500" />
                  )}
                </span>
                <span className="break-words">{v.description}</span>
              </label>
            </div>
          );
        })}
      </fieldset>
    );
  }

  function renderAttachments() {
    if (formik.values.attachement.length > 0) {
      return (
        <div className="mt-4">
          <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
            Attachments
          </p>
          <div className="flex flex-wrap gap-2">
            {(formik.values.attachement as any[]).map((v: any, idx: number) => {
              const type = getAttachmentType(v.url);
              const label =
                formik.values.attachement.length > 1
                  ? `Attachment ${idx + 1}`
                  : "View Attachment";

              if (type === "image") {
                return (
                  <button
                    key={v.url}
                    type="button"
                    onClick={() => setLightboxUrl(v.url)}
                    className="block relative w-28 h-20 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:opacity-90 transition-opacity flex-shrink-0"
                    title={label}
                  >
                    <img
                      src={v.url}
                      alt={label}
                      className="w-full h-full object-cover"
                    />
                  </button>
                );
              }

              if (type === "video") {
                return (
                  <a
                    key={v.url}
                    href={v.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block relative w-28 h-20 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:opacity-90 transition-opacity flex-shrink-0"
                    title={label}
                  >
                    <video
                      src={v.url}
                      className="w-full h-full object-cover"
                      muted
                      preload="metadata"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-white/90">
                        <svg
                          className="w-4 h-4 text-gray-800 ml-0.5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                        </svg>
                      </span>
                    </div>
                  </a>
                );
              }

              return (
                <a
                  key={v.url}
                  target="_blank"
                  rel="noreferrer"
                  href={v.url}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/50 rounded-lg text-xs font-medium text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                    />
                  </svg>
                  {label}
                </a>
              );
            })}
          </div>
        </div>
      );
    } else {
      return <div></div>;
    }
  }

  function isDisabled() {
    if (formik.values.pollOptions?.length > 0) {
      return (
        formik.values.selectedAnswer === undefined ||
        formik.values.selectedAnswer === "" ||
        formik.values.selectedAnswer === null
      );
    }
    return loading;
  }

  function renderPollError() {
    const needsSelection =
      formik.values.pollOptions !== undefined &&
      formik.values.pollOptions.length > 0 &&
      (formik.values.selectedAnswer === undefined ||
        formik.values.selectedAnswer === "" ||
        formik.values.selectedAnswer === null);

    if (!needsSelection) return null;
    return (
      <p className="text-xs text-red-500 dark:text-red-400 mb-3">
        Please select an option to continue
      </p>
    );
  }

  function buttonMessage() {
    if (loading) {
      return "Submitting...";
    } else if (
      formik.values.pollOptions &&
      formik.values.pollOptions.length > 0
    ) {
      return "Submit Your Answer";
    } else if (
      !formik.values.hide_comment_box &&
      formik.values.confirmed_at !== null
    ) {
      return "Update Your Answer";
    } else {
      return "Confirm Message";
    }
  }

  function buttonView() {
    if (
      formik.values.confirmed_at !== null &&
      formik.values.hide_comment_box &&
      formik.values.pollOptions.length === 0
    ) {
      return null;
    }
    if (
      formik.values.pollOptions.length === 0 &&
      formik.values.hide_confirmation
    ) {
      return null;
    }
    return (
      <button
        type="submit"
        disabled={isDisabled() || loading}
        className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-200 dark:disabled:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed text-gray-900 font-semibold text-sm py-3.5 rounded-xl transition-colors duration-150"
      >
        {loading && <ButtonSpinner />}
        <span>{buttonMessage()}</span>
      </button>
    );
  }

  return pageLoading ? (
    <PageLoadingView isDark={isDark} toggle={toggle} />
  ) : (
    <div className="min-h-screen bg-slate-100 dark:bg-gray-950 flex items-center justify-center px-4 py-4 transition-colors duration-200">
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            type="button"
            onClick={() => setLightboxUrl(null)}
            aria-label="Close image"
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <img
            src={lightboxUrl}
            alt="Attachment"
            className="max-w-full max-h-full rounded-xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      <div className="w-full max-w-[920px]">
        {/* Card — flex column with max-height so body scrolls */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-md overflow-hidden flex flex-col max-h-[calc(100vh-2rem)] transition-colors duration-200">
          <div className="h-1 bg-orange-500 flex-shrink-0" />

          <form
            onSubmit={formik.handleSubmit}
            className="flex flex-col flex-1 min-h-0 font-latoRegular"
          >
            {/* ── Sticky header: logo + org name + dark mode toggle ── */}
            <div className="flex-shrink-0 flex items-center gap-3 px-7 sm:px-10 pt-7 pb-5 border-b border-gray-100 dark:border-gray-800">
              {!formik.values.organizationSmallLogo || logoError ? (
                <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-base leading-none">
                    {getInitials(formik.values.organizationName)}
                  </span>
                </div>
              ) : (
                <img
                  src={formik.values.organizationSmallLogo}
                  alt="Organization logo"
                  className="max-h-12 max-w-[160px] object-contain flex-shrink-0"
                  onError={() => setLogoError(true)}
                />
              )}
              {formik.values.organizationName && (
                <h1 className="flex-1 text-base font-bold text-gray-800 dark:text-gray-100 leading-tight">
                  {formik.values.organizationName}
                </h1>
              )}
              {/* Dark mode toggle lives in the header */}
              <button
                type="button"
                onClick={toggle}
                aria-label="Toggle dark mode"
                className="ml-auto flex-shrink-0 p-2 rounded-full text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-150"
              >
                {isDark ? (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                    />
                  </svg>
                )}
              </button>
            </div>

            {/* ── Scrollable body ── */}
            <div className="flex-1 overflow-y-auto poll-scroll px-7 sm:px-10 py-6">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                {formik.values.title}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {linkifyText(formik.values.description)}
              </p>

              {renderMetadata()}
              {renderAttachments()}

              {/* Poll options */}
              {formik.values.pollOptions &&
                formik.values.pollOptions.length > 0 && (
                  <div className="mt-6">
                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
                      Select your response
                    </p>
                    {renderPollOptions()}
                  </div>
                )}

              {/* Comment */}
              {!formik.values.hide_comment_box && (
                <div className="mt-6">
                  <label
                    className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2"
                    htmlFor="comment"
                  >
                    Comment
                  </label>
                  <textarea
                    id="comment"
                    rows={4}
                    className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl p-3 text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none transition-colors duration-150"
                    name="comment"
                    placeholder="Add a comment (optional)..."
                    value={formik.values.comment}
                    onChange={formik.handleChange}
                  />
                </div>
              )}
            </div>

            {/* ── Sticky footer: error + submit button ── */}
            <div className="flex-shrink-0 px-7 sm:px-10 pt-4 pb-6 sm:pb-8 border-t border-gray-100 dark:border-gray-800">
              {renderPollError()}
              {buttonView()}
              <p className="text-center text-xs text-gray-400 dark:text-gray-600 mt-4">
                Powered by AlertAware
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default PollDisplayer;
